import { type IProcessedBudgetData } from '@webapp/components/Budgets/SpendTracker'
import { type TimezonesEnum } from '@webapp/core/entities/external/enums/TimezonesEnum'
import { convertISOStringToTimezone } from '@webapp/core/utils/dates/convertISOStringToTimezone'
import { type BudgetFormat } from '@webapp/hooks'
import { type BudgetCategory } from '@webapp/platform/budgets'
import { stringifyAccountCode } from './stringifyAccountCode'
import { stringifySecondaryCategory } from './stringifySecondaryCategory'

export const formatBudgetCategories = ({
  budgetCategories,
  headquartersTimezone,
  calculateBudgetSpendDetails,
}: {
  budgetCategories: BudgetCategory[]
  headquartersTimezone: TimezonesEnum
  calculateBudgetSpendDetails: (budget: {
    id: number
    amount: string | null
    approved: string
    pending: string
    purchased: string
    invoiced: string
    received: string
    request_pending_amount: string
    start_datetime: Nullable<ISODateString>
    end_datetime: Nullable<ISODateString>
    variance: string | null
    variance_percentage: string | null
  }) => BudgetFormat
}): IProcessedBudgetData[] => {
  return budgetCategories.map((budget) => {
    // Start and end dates are set according to the timezone of the headquarters which may differ from the user. As such, we
    // need to explicitly convert the dates to the headquarters timezone.
    const adjustedStartDate = budget.startDate
      ? convertISOStringToTimezone(
          budget.startDate.toISOString(),
          headquartersTimezone
        )
      : null
    const adjustedEndDate = budget.endDate
      ? convertISOStringToTimezone(
          budget.endDate.toISOString(),
          headquartersTimezone
        )
      : null

    const budgetSpendDetails = calculateBudgetSpendDetails({
      id: budget.id,
      amount: budget.amount,
      approved: budget.spend.approvedSpend,
      pending: budget.spend.pendingSpend,
      purchased: budget.spend.purchasedSpend,
      invoiced: budget.spend.invoicedSpend,
      received: budget.spend.receivedSpend,
      request_pending_amount: budget.spend.currentRequestPendingSpend,
      start_datetime: budget.startDate ? budget.startDate.toISOString() : null,
      end_datetime: budget.endDate ? budget.endDate.toISOString() : null,
      variance: budget.varianceAmount,
      variance_percentage: budget.variancePercentage,
    })

    return {
      startDate: adjustedStartDate,
      endDate: adjustedEndDate,
      budgetSpendDetails,
      name: budget.name,
      uuid: budget.uuid,
      period: budget.interval,
      attributes: {
        departments: budget.secondaryCategories.map(stringifySecondaryCategory),
        accountCodes: budget.accountCodes.map(stringifyAccountCode),
      },
    }
  })
}
